import { registerTheme } from 'echarts/core'
import { computed, shallowRef, watch, type ComputedRef, type ShallowRef } from 'vue'

import '@/config/echarts'

export const OMR_ECHARTS_THEME = 'omr'

export interface OmrChartTokens {
  palette: string[]
  dataLabel: string
  axisLabel: string
  gridLine: string
  axisLine: string
  surface: string
  muted: string
  warning: string
  success: string
  error: string
}

export interface UseOmrEchartsTheme {
  themeName: typeof OMR_ECHARTS_THEME
  rootRef: ShallowRef<HTMLElement | null>
  tokens: ShallowRef<OmrChartTokens | undefined>
  ready: ComputedRef<boolean>
}

let themeRegistered = false

function hasAllTokens(tokens: OmrChartTokens): boolean {
  return [
    ...tokens.palette,
    tokens.dataLabel,
    tokens.axisLabel,
    tokens.gridLine,
    tokens.axisLine,
    tokens.surface,
    tokens.muted,
    tokens.warning,
    tokens.success,
    tokens.error,
  ].every(Boolean)
}

function resolveTokens(element: HTMLElement): OmrChartTokens {
  const styles = getComputedStyle(element)
  const read = (name: string): string => styles.getPropertyValue(name).trim()

  return {
    palette: [
      read('--graphic-color-primary-strong'),
      read('--graphic-color-secondary-strong'),
      read('--graphic-color-tertiary-strong'),
      read('--graphic-color-primary-subtle'),
      read('--graphic-color-secondary-medium'),
    ],
    dataLabel: read('--text-color-main'),
    axisLabel: read('--text-color-subtle'),
    gridLine: read('--graphic-color-grey-subtle'),
    axisLine: read('--graphic-color-grey-medium'),
    surface: read('--background-color-surface'),
    muted: read('--background-color-surface-elevation-4'),
    warning: read('--border-color-warning'),
    success: read('--graphic-color-success'),
    error: read('--graphic-color-error'),
  }
}

// Single-theme assumption: the `omr` theme is registered once, globally, from the
// first chart that mounts. Its axis/legend/label/grid colours are then shared by every
// chart, while series colours come from each instance's own `tokens`. This holds because
// all OMR charts live under the app's `.light-theme` wrapper. A chart mounted inside a
// `dark-theme` subtree would mix the frozen (light) chrome with its own (dark) series —
// re-key the registration by resolved tokens if that ever becomes a real surface.
function registerOmrTheme(tokens: OmrChartTokens): void {
  if (themeRegistered) return

  registerTheme(OMR_ECHARTS_THEME, {
    color: tokens.palette,
    backgroundColor: 'transparent',
    textStyle: { color: tokens.dataLabel, fontFamily: getComputedStyle(document.body).fontFamily },
    categoryAxis: {
      axisLine: { lineStyle: { color: tokens.axisLine } },
      axisTick: { lineStyle: { color: tokens.axisLine } },
      axisLabel: { color: tokens.axisLabel },
      splitLine: { show: false, lineStyle: { color: tokens.gridLine } },
    },
    valueAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: tokens.axisLabel },
      splitLine: { lineStyle: { color: tokens.gridLine } },
    },
    legend: { textStyle: { color: tokens.dataLabel } },
  })

  themeRegistered = true
}

// OMR tokens live on `.light-theme`, not `:root` — bind `rootRef` to an element
// inside `.light-theme`. Outside it the tokens resolve empty; the guard below then
// warns and keeps `ready` false rather than registering an all-black theme.
export function useOmrEchartsTheme(): UseOmrEchartsTheme {
  const rootRef = shallowRef<HTMLElement | null>(null)
  const tokens = shallowRef<OmrChartTokens>()

  // watch (not onMounted) so a rootRef that mounts late — behind a v-if, a loading gate,
  // a collapsed panel — still initialises once its element appears.
  watch(
    rootRef,
    (element) => {
      if (!element) return
      const resolved = resolveTokens(element)
      if (!hasAllTokens(resolved)) {
        console.warn('[useOmrEchartsTheme] OMR chart tokens resolved empty — bind rootRef to an element inside `.light-theme`.')
        return
      }
      tokens.value = resolved
      registerOmrTheme(resolved)
    },
    { immediate: true, flush: 'post' },
  )

  const ready = computed(() => tokens.value !== undefined)

  return { themeName: OMR_ECHARTS_THEME, rootRef, tokens, ready }
}
